/**
 * Open a bundle a thousand times and look at what comes out.
 *
 * Nothing is written. The server runs the REAL selector — the same two
 * functions a live open calls — so what this shows is what will happen,
 * rather than a model of it.
 *
 * That distinction is the whole reason the screen exists. stefancoat has a
 * simulator with a better UI than this one, and it is a second implementation
 * in the browser over a per-slot model its own build endpoint discards before
 * it reaches the database: `build-bundle-from-box` transmits the box size and
 * nothing else, writes an equal chance for every reward, then turns on
 * availability weighting which overrides even that. Three distributions, and
 * the preview predicts none of them.
 *
 * Two numbers here answer "will the last pack be whole", and they are
 * different numbers, so both are shown and both are labelled:
 *
 *   max_complete_packs  the guarantee — what survives ANY draw order
 *   exhausted_after     what this run actually got before it ran dry
 */
import { useState, type ReactNode } from 'react';
import type { BundleCapacity, Reward, SimulationResult, SlotOutcome } from '@tomo/contracts';
import { Shell } from '../components/Shell.jsx';
import { Badge, Button, Failed, Link, Spinner } from '../components/primitives.jsx';
import { Num } from '../components/form.jsx';
import { packs, rewards as rewardsApi } from '../lib/tomo.js';
import { ApiError } from '../lib/api.js';
import { useAsync } from '../useAsync.js';
import { hrefFor } from '../lib/router.js';

export function BundleSimulator(
  { channelId, bundleId }: { channelId: string; bundleId: string }
): ReactNode {
  const [loaded] = useAsync(
    async (signal) => ({
      capacity: await packs.capacity(channelId, bundleId, signal),
      rewards: await rewardsApi.list(channelId, signal),
    }),
    [channelId, bundleId]
  );

  if (loaded.state === 'loading') {
    return <Shell channelId={channelId}><Spinner label="Reading the catalog…" /></Shell>;
  }
  if (loaded.state === 'failed') {
    return (
      <Shell channelId={channelId}>
        <Failed message="Could not read this bundle." detail={loaded.error.message} />
      </Shell>
    );
  }
  return (
    <Panel
      channelId={channelId}
      bundleId={bundleId}
      capacity={loaded.value.capacity}
      rewards={loaded.value.rewards}
    />
  );
}

function Panel(
  { channelId, bundleId, capacity, rewards }: {
    channelId: string; bundleId: string; capacity: BundleCapacity; rewards: Reward[];
  }
): ReactNode {
  const [runs, setRuns] = useState(1000);
  const [result, setResult] = useState<SimulationResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const nameOf = (id: string): string => rewards.find((r) => r.id === id)?.name ?? id;

  const run = (): void => {
    setBusy(true);
    setError(null);
    packs.simulate(channelId, bundleId, { runs }).then(
      (res) => { setResult(res); setBusy(false); },
      (err: unknown) => {
        setBusy(false);
        setError(err instanceof ApiError
          ? err
          : new ApiError('malformed_response', 0, 'Something unexpected went wrong.'));
      }
    );
  };

  return (
    <Shell channelId={channelId}>
      <div className="flex items-baseline justify-between gap-4">
        <h1 className="text-xl font-semibold tracking-tight">Simulate opens</h1>
        <Link href={hrefFor([channelId, 'bundles', bundleId])}>Back to the bundle</Link>
      </div>
      <p className="mt-1 text-sm text-ink-400">
        Nothing is written. The server runs the same selector a live open runs, so
        what you see here is what will happen.
      </p>

      <Capacity capacity={capacity} nameOf={nameOf} />

      {error ? <div className="mt-6"><Failed message={error.message} /></div> : null}

      <div className="mt-8 flex items-end gap-3">
        <span className="w-32">
          <span className="mb-1 block text-xs text-ink-400">Opens</span>
          <Num value={runs} onChange={setRuns} min={1} max={10000} />
        </span>
        <Button onClick={run} disabled={busy || !capacity.can_fill}>
          {busy ? 'Opening…' : `Open ${runs.toLocaleString()}`}
        </Button>
      </div>

      {!capacity.can_fill ? (
        <p className="mt-3 text-xs text-warn-400">
          This bundle cannot fill right now, so there is nothing to simulate.
        </p>
      ) : null}

      {result ? <Outcome result={result} nameOf={nameOf} /> : null}
    </Shell>
  );
}

function Capacity(
  { capacity, nameOf }: { capacity: BundleCapacity; nameOf: (id: string) => string }
): ReactNode {
  return (
    <section className="mt-6 rounded-lg border border-ink-800 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={capacity.can_fill ? 'good' : 'warn'}>
          {capacity.can_fill ? 'Can open' : 'Cannot open'}
        </Badge>
        <Badge>{capacity.pack_size} per pack</Badge>
        <Badge tone="brand">
          {capacity.max_complete_packs === null
            ? 'unlimited packs'
            : `${capacity.max_complete_packs.toLocaleString()} whole packs guaranteed`}
        </Badge>
      </div>

      {capacity.max_complete_packs !== null ? (
        <p className="mt-2 text-xs text-ink-400">
          Guaranteed, not hoped for: that many survive <em>any</em> order the draws
          come out in, so a luckier run does better and none does worse.
        </p>
      ) : null}

      {capacity.stranded_stock > 0 ? (
        <p className="mt-2 text-sm">
          <strong>{capacity.stranded_stock.toLocaleString()}</strong> copies will be
          stranded — they exist, but can never complete a pack.
        </p>
      ) : null}

      {capacity.empty_slots.length > 0 ? (
        <p className="mt-2 text-sm text-warn-400">
          {/* Naming the slot sends an operator to the one line to change,
              rather than to read their whole configuration (L66). */}
          Nothing can fill{' '}
          {capacity.empty_slots
            .map((s) => s.label ?? `slot ${String(s.slot_index + 1)}`)
            .join(', ')}
          .
        </p>
      ) : null}

      {capacity.scarcest ? (
        <p className="mt-2 text-xs text-ink-400">
          Runs dry first: {nameOf(capacity.scarcest.reward_id)} in slot{' '}
          {capacity.scarcest.slot_index + 1} · {capacity.scarcest.remaining.toLocaleString()} left
        </p>
      ) : null}
    </section>
  );
}

function Outcome(
  { result, nameOf }: { result: SimulationResult; nameOf: (id: string) => string }
): ReactNode {
  return (
    <div className="mt-8 space-y-6">
      <section>
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="brand">{result.packs.toLocaleString()} whole packs</Badge>
          <Badge>{result.items.toLocaleString()} collectibles</Badge>
          {result.exhausted_after !== null ? (
            <Badge tone="warn">ran dry after {result.exhausted_after.toLocaleString()}</Badge>
          ) : null}
        </div>
        {result.exhausted_after !== null ? (
          <p className="mt-2 text-xs text-ink-400">
            {/* The pack that could not be filled was not handed over short — it
                was not handed over. Same rule as a live open. */}
            The next pack could not fill every slot, so it was not delivered at all
            and the run stopped. No partial pack is ever handed out.
          </p>
        ) : null}
      </section>

      {Object.keys(result.by_tier).length > 1 || result.by_tier[''] === undefined ? (
        <section>
          <h2 className="text-sm font-medium uppercase tracking-wide text-ink-400">By tier</h2>
          <Distribution counts={result.by_tier} total={result.items} label={(k) => k || 'untiered'} />
        </section>
      ) : null}

      <section>
        <h2 className="text-sm font-medium uppercase tracking-wide text-ink-400">By slot</h2>
        {result.expected_drifts ? (
          <p className="mt-1 text-xs text-ink-400">
            {/* A by_remaining slot re-derives its odds every draw, so the
                configured share is the odds on the FIRST open rather than a
                constant. Showing a deviation against it without saying this
                would look like a bug in the draw. */}
            At least one slot takes its odds from remaining stock, so “configured”
            below is the odds on the <em>first</em> open. They shift as the pool drains.
          </p>
        ) : null}
        <div className="mt-3 space-y-4">
          {result.slots.map((slot) => (
            <SlotTable key={slot.slot_index} slot={slot} nameOf={nameOf} packs={result.packs} />
          ))}
        </div>
      </section>
    </div>
  );
}

function SlotTable(
  { slot, nameOf, packs: packCount }: {
    slot: SlotOutcome; nameOf: (id: string) => string; packs: number;
  }
): ReactNode {
  const drawn = Object.values(slot.by_reward).reduce((n, v) => n + v, 0);
  const ids = [...new Set([...Object.keys(slot.by_reward), ...Object.keys(slot.expected)])];

  return (
    <div className="rounded-lg border border-ink-800">
      <div className="flex items-center gap-2 border-b border-ink-800 px-4 py-2 text-sm">
        <span className="tabular-nums text-ink-400">{slot.slot_index + 1}</span>
        <span className="flex-1">{slot.label ?? <span className="text-ink-400">Unlabelled</span>}</span>
        <span className="text-xs text-ink-400">
          {drawn.toLocaleString()} drawn over {packCount.toLocaleString()} packs
        </span>
      </div>
      <table className="w-full text-sm">
        <tbody>
          {ids.map((id) => {
            const count = slot.by_reward[id] ?? 0;
            const actual = drawn === 0 ? 0 : count / drawn;
            const expected = slot.expected[id];
            return (
              <tr key={id} className="border-t border-ink-800/60">
                <td className="px-4 py-1.5">{nameOf(id)}</td>
                <td className="px-4 py-1.5 text-right tabular-nums">{count.toLocaleString()}</td>
                <td className="px-4 py-1.5 text-right tabular-nums">{pct(actual)}</td>
                <td className="px-4 py-1.5 text-right tabular-nums text-ink-400">
                  {expected === undefined ? '—' : pct(expected)}
                </td>
                <td className={`px-4 py-1.5 text-right tabular-nums ${driftTone(actual, expected)}`}>
                  {expected === undefined ? '' : signed(actual - expected)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Distribution(
  { counts, total, label }: {
    counts: Record<string, number>; total: number; label: (key: string) => string;
  }
): ReactNode {
  const rows = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  return (
    <table className="mt-3 w-full text-sm">
      <tbody>
        {rows.map(([key, count]) => (
          <tr key={key} className="border-t border-ink-800/60">
            <td className="py-1.5">{label(key)}</td>
            <td className="py-1.5 text-right tabular-nums">{count.toLocaleString()}</td>
            <td className="py-1.5 text-right tabular-nums text-ink-400">
              {pct(total === 0 ? 0 : count / total)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const pct = (n: number): string => `${(n * 100).toFixed(1)}%`;
const signed = (n: number): string => `${n >= 0 ? '+' : ''}${(n * 100).toFixed(1)}`;

/**
 * Colour the deviation, so a table of numbers reads at a glance.
 *
 * The thresholds are stefancoat's, which were chosen against real boxes and
 * are the one part of its simulator worth copying outright.
 */
function driftTone(actual: number, expected: number | undefined): string {
  if (expected === undefined) return '';
  const drift = Math.abs(actual - expected);
  if (drift <= 0.01) return 'text-good-400';
  if (drift <= 0.03) return 'text-warn-400';
  return 'text-bad-400';
}
